import ProfessionalIntegralProfile from "./ProfessionalIntegralProfile";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  FormEvent,
  MouseEvent,
} from "react";
import { api, ApiClientError } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import type {
  Id,
  ProfessionalMaster,
  Specialty,
  StaffUser,
} from "../types";

type ProfessionalForm = {
  full_name: string;
  user_id: string;
  active: boolean;
  preferred_name: string;
  public_title: string;
  public_bio: string;
  profile_image_url: string;
  specialty_ids: string[];
  primary_specialty_id: string;
};

const EMPTY_FORM: ProfessionalForm = {
  full_name: "",
  user_id: "",
  active: true,
  preferred_name: "",
  public_title: "",
  public_bio: "",
  profile_image_url: "",
  specialty_ids: [],
  primary_specialty_id: "",
};

function idText(value: Id | null | undefined) {
  return value == null ? "" : String(value);
}

function errorMessage(
  error: unknown,
  fallback: string
) {
  return error instanceof ApiClientError
    ? error.message
    : fallback;
}

export default function ProfessionalsConfig() {
  const { hasPermission } = useAuth();
  const { show } = useToast();

  const canView =
    hasPermission("professionals.view");

  const canManage =
    hasPermission("professionals.manage");

  const canViewSensitive =
    hasPermission(
      "professionals.sensitive.view"
    );

  const [professionals, setProfessionals] =
    useState<ProfessionalMaster[]>([]);

  const [specialties, setSpecialties] =
    useState<Specialty[]>([]);

  const [users, setUsers] =
    useState<StaffUser[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [submitting, setSubmitting] =
    useState(false);

  const [showForm, setShowForm] =
    useState(false);

  const [editing, setEditing] =
    useState<ProfessionalMaster | null>(
      null
    );

  const [selectedProfessional, setSelectedProfessional] =
    useState<ProfessionalMaster | null>(
      null
    );

  const [form, setForm] =
    useState<ProfessionalForm>({
      ...EMPTY_FORM,
    });

  const [showSpecialtyForm, setShowSpecialtyForm] =
    useState(false);

  const [specialtyName, setSpecialtyName] =
    useState("");

  const [specialtyCategory, setSpecialtyCategory] =
    useState("");

  const [specialtyDescription, setSpecialtyDescription] =
    useState("");

  const [savingSpecialty, setSavingSpecialty] =
    useState(false);

  const availableUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          user.role_code === "professional" &&
          user.active &&
          (
            !user.professional_id ||
            idText(user.professional_id) ===
              idText(editing?.id)
          )
      ),
    [users, editing]
  );

  const load = useCallback(async () => {
    if (!canView) {
      return;
    }

    setLoading(true);

    try {
      const [
        professionalRows,
        specialtyRows,
        userRows,
      ] = await Promise.all([
        api.professionalMasterList(),
        api.professionalSpecialties(),
        canManage
          ? api.users()
          : Promise.resolve([]),
      ]);

      setProfessionals(professionalRows);
      setSpecialties(specialtyRows);
      setUsers(userRows);

      setSelectedProfessional(
        (current) => {
          if (!current) {
            return null;
          }

          return (
            professionalRows.find(
              (item) =>
                idText(item.id) ===
                idText(current.id)
            ) ?? null
          );
        }
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudieron cargar los profesionales."
        ),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [
    canManage,
    canView,
    show,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setShowForm(true);
  }

  function openEdit(
    professional: ProfessionalMaster
  ) {
    setEditing(professional);

    setForm({
      full_name:
        professional.full_name,
      user_id:
        idText(professional.user_id),
      active: professional.active,
      preferred_name:
        professional.preferred_name || "",
      public_title:
        professional.public_title || "",
      public_bio:
        professional.public_bio || "",
      profile_image_url:
        professional.profile_image_url || "",
      specialty_ids:
        professional.specialties.map(
          (specialty) =>
            idText(specialty.id)
        ),
      primary_specialty_id:
        idText(
          professional.primary_specialty_id
        ),
    });

    setShowForm(true);
  }

  function closeForm() {
    if (submitting) {
      return;
    }

    setShowForm(false);
    setEditing(null);
    setForm({ ...EMPTY_FORM });
  }

  function toggleSpecialty(
    specialtyId: Id
  ) {
    const value = idText(specialtyId);

    setForm((current) => {
      const selected =
        current.specialty_ids.includes(
          value
        );

      const specialtyIds = selected
        ? current.specialty_ids.filter(
            (id) => id !== value
          )
        : [
            ...current.specialty_ids,
            value,
          ];

      let primaryId =
        current.primary_specialty_id;

      if (
        selected &&
        primaryId === value
      ) {
        primaryId =
          specialtyIds.length === 1
            ? specialtyIds[0]
            : "";
      }

      if (
        !selected &&
        specialtyIds.length === 1
      ) {
        primaryId = specialtyIds[0];
      }

      return {
        ...current,
        specialty_ids: specialtyIds,
        primary_specialty_id:
          primaryId,
      };
    });
  }

  async function submitProfessional(
    event: any
  ) {
    event.preventDefault();

    if (!form.full_name.trim()) {
      show(
        "Ingresá el nombre completo.",
        "error"
      );
      return;
    }

    if (
      form.primary_specialty_id &&
      !form.specialty_ids.includes(
        form.primary_specialty_id
      )
    ) {
      show(
        "La especialidad principal debe estar seleccionada.",
        "error"
      );
      return;
    }

    setSubmitting(true);

    const payload = {
      full_name:
        form.full_name.trim(),
      user_id:
        form.user_id || null,
      active: form.active,
      specialty_ids:
        form.specialty_ids,
      primary_specialty_id:
        form.primary_specialty_id ||
        null,
      profile: {
        preferred_name:
          form.preferred_name.trim() ||
          null,
        public_title:
          form.public_title.trim() ||
          null,
        public_bio:
          form.public_bio.trim() ||
          null,
        profile_image_url:
          form.profile_image_url.trim() ||
          null,
      },
    };

    try {
      if (editing) {
        await api.updateProfessional(
          editing.id,
          payload
        );

        show(
          "Profesional actualizado.",
          "success"
        );
      } else {
        await api.createProfessional({
          ...payload,
          full_name:
            form.full_name.trim(),
        });

        show(
          "Profesional creado.",
          "success"
        );
      }

      setShowForm(false);
      setEditing(null);
      setForm({ ...EMPTY_FORM });
      await load();
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo guardar el profesional."
        ),
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleProfessionalActive(
    professional: ProfessionalMaster
  ) {
    try {
      await api.updateProfessional(
        professional.id,
        {
          active:
            !professional.active,
        }
      );

      show(
        professional.active
          ? "Profesional desactivado."
          : "Profesional activado.",
        "success"
      );

      await load();
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo actualizar el profesional."
        ),
        "error"
      );
    }
  }

  async function submitSpecialty(
    event: any
  ) {
    event.preventDefault();

    if (!specialtyName.trim()) {
      show(
        "Ingresá el nombre de la especialidad.",
        "error"
      );
      return;
    }

    setSavingSpecialty(true);

    try {
      const created =
        await api.createProfessionalSpecialty(
          {
            name:
              specialtyName.trim(),
            category:
              specialtyCategory.trim() ||
              null,
            description:
              specialtyDescription.trim() ||
              null,
          }
        );

      setSpecialties((current) =>
        [...current, created].sort(
          (left, right) =>
            left.name.localeCompare(
              right.name,
              "es"
            )
        )
      );

      setForm((current) => ({
        ...current,
        specialty_ids: [
          ...current.specialty_ids,
          idText(created.id),
        ],
        primary_specialty_id:
          current.primary_specialty_id ||
          idText(created.id),
      }));

      setSpecialtyName("");
      setSpecialtyCategory("");
      setSpecialtyDescription("");
      setShowSpecialtyForm(false);

      show(
        "Especialidad creada y seleccionada.",
        "success"
      );
    } catch (error) {
      show(
        errorMessage(
          error,
          "No se pudo crear la especialidad."
        ),
        "error"
      );
    } finally {
      setSavingSpecialty(false);
    }
  }

  if (!canView) {
    return (
      <div className="empty-state">
        No tenés permiso para consultar profesionales.
      </div>
    );
  }

  return (
    <div>
      <div className="kanban-header">
        <div>
          <h2>Profesionales</h2>
          <p className="meta">
            Maestro único del equipo profesional:
            identidad, especialidades, acceso al CRM
            y documentación laboral.
          </p>
        </div>

        {canManage && (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="btn-ghost"
              onClick={() =>
                setShowSpecialtyForm(true)
              }
            >
              + Especialidad
            </button>

            <button
              type="button"
              className="btn-primary"
              onClick={openNew}
            >
              + Nuevo profesional
            </button>
          </div>
        )}
      </div>

      <div
        style={{
          padding: 14,
          marginBottom: 18,
          borderRadius: 12,
          background: "#f4f7f8",
          border: "1px solid #dce5e8",
        }}
      >
        <strong>
          Profesionales y usuarios son conceptos distintos.
        </strong>

        <p
          className="meta"
          style={{ marginBottom: 0 }}
        >
          Un profesional puede existir sin acceso al
          sistema. Si necesita ingresar al CRM, se lo
          vincula con un usuario cuyo rol sea
          Profesional.
        </p>
      </div>

      {loading && (
        <div className="loading-strip">
          Cargando profesionales...
        </div>
      )}

      <div className="contact-list">
        {professionals.map(
          (professional) => (
            <div
              key={professional.id}
              className="contact-row"
              style={{
                alignItems: "flex-start",
                gap: 16,
              }}
            >
              <div style={{ flex: 1 }}>
                <div className="name">
                  {professional.preferred_name ||
                    professional.full_name}

                  {!professional.active &&
                    " · Inactivo"}
                </div>

                {professional.public_title && (
                  <div className="meta">
                    {professional.public_title}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    marginTop: 8,
                  }}
                >
                  {professional.specialties.map(
                    (specialty) => (
                      <span
                        key={specialty.id}
                        style={{
                          padding:
                            "4px 9px",
                          borderRadius: 999,
                          background:
                            specialty.is_primary
                              ? "#dcecf1"
                              : "#eef2f3",
                          color: "#173245",
                          fontSize: 12,
                          fontWeight:
                            specialty.is_primary
                              ? 700
                              : 500,
                        }}
                      >
                        {specialty.name}
                        {specialty.is_primary
                          ? " · Principal"
                          : ""}
                      </span>
                    )
                  )}

                  {professional.specialties
                    .length === 0 && (
                    <span className="meta">
                      Sin especialidad asignada
                    </span>
                  )}
                </div>

                <div
                  className="meta"
                  style={{ marginTop: 9 }}
                >
                  {professional.has_user
                    ? "Acceso al CRM vinculado"
                    : "Sin usuario de acceso"}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  justifyContent:
                    "flex-end",
                }}
              >
                {canViewSensitive && (
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() =>
                      setSelectedProfessional(
                        professional
                      )
                    }
                  >
                    Ficha integral
                  </button>
                )}

                {canManage && (
                  <>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() =>
                        openEdit(
                          professional
                        )
                      }
                    >
                      Editar
                    </button>

                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() =>
                        toggleProfessionalActive(
                          professional
                        )
                      }
                    >
                      {professional.active
                        ? "Desactivar"
                        : "Activar"}
                    </button>
                  </>
                )}
              </div>
            </div>
          )
        )}

        {!loading &&
          professionals.length === 0 && (
            <div className="empty-state">
              Todavía no hay profesionales
              cargados.
            </div>
          )}
      </div>

      {selectedProfessional &&
        canViewSensitive && (
          <ProfessionalIntegralProfile
            professional={
              selectedProfessional
            }
            onClose={() =>
              setSelectedProfessional(null)
            }
          />
        )}

      {showForm && (
        <div
          className="modal-backdrop"
          onClick={closeForm}
        >
          <div
            className="modal modal-wide"
            onClick={(
              event: any
            ) => event.stopPropagation()}
          >
            <h3>
              {editing
                ? "Editar profesional"
                : "Nuevo profesional"}
            </h3>

            <form
              className="form-grid"
              onSubmit={
                submitProfessional
              }
            >
              <label>
                Nombre completo
              </label>

              <input
                value={form.full_name}
                onChange={(event: any) =>
                  setForm((current) => ({
                    ...current,
                    full_name:
                      event.target.value,
                  }))
                }
                placeholder="Ej: Dra. Carolina Tartara"
                required
              />

              <label>
                Usuario de acceso
              </label>

              <select
                value={form.user_id}
                onChange={(event: any) =>
                  setForm((current) => ({
                    ...current,
                    user_id:
                      event.target.value,
                  }))
                }
              >
                <option value="">
                  Sin usuario de acceso
                </option>

                {availableUsers.map(
                  (user) => (
                    <option
                      key={user.id}
                      value={idText(user.id)}
                    >
                      {user.full_name} ·{" "}
                      {user.email}
                    </option>
                  )
                )}
              </select>

              <span />

              <p className="meta">
                Solo aparecen usuarios activos
                cuyo rol es Profesional y que no
                están vinculados a otra ficha.
              </p>

              <label>
                Nombre preferido
              </label>

              <input
                value={
                  form.preferred_name
                }
                onChange={(event: any) =>
                  setForm((current) => ({
                    ...current,
                    preferred_name:
                      event.target.value,
                  }))
                }
                placeholder="Ej: Dra. Carolina"
              />

              <label>
                Título público
              </label>

              <input
                value={form.public_title}
                onChange={(event: any) =>
                  setForm((current) => ({
                    ...current,
                    public_title:
                      event.target.value,
                  }))
                }
                placeholder="Ej: Médica especialista en estética"
              />

              <label>
                Presentación pública
              </label>

              <textarea
                value={form.public_bio}
                onChange={(event: any) =>
                  setForm((current) => ({
                    ...current,
                    public_bio:
                      event.target.value,
                  }))
                }
                placeholder="Resumen profesional que podrá utilizarse en comunicaciones y perfiles."
                rows={4}
              />

              <label>
                URL de imagen
              </label>

              <input
                value={
                  form.profile_image_url
                }
                onChange={(event: any) =>
                  setForm((current) => ({
                    ...current,
                    profile_image_url:
                      event.target.value,
                  }))
                }
                placeholder="https://..."
              />

              <label>
                Especialidades
              </label>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                }}
              >
                {specialties.map(
                  (specialty) => {
                    const value =
                      idText(
                        specialty.id
                      );

                    return (
                      <label
                        key={specialty.id}
                        style={{
                          display: "flex",
                          alignItems:
                            "center",
                          gap: 8,
                          fontWeight: 400,
                        }}
                      >
                        <input
                          type="checkbox"
                      style={{
                        width: "auto",
                        flex: "0 0 auto",
                        margin: 0,
                      }}
                          checked={form.specialty_ids.includes(
                            value
                          )}
                          onChange={() =>
                            toggleSpecialty(
                              specialty.id
                            )
                          }
                        />

                        <span>
                          {specialty.name}
                          {specialty.category
                            ? ` · ${specialty.category}`
                            : ""}
                        </span>
                      </label>
                    );
                  }
                )}

                {specialties.length ===
                  0 && (
                  <p className="meta">
                    Primero creá una
                    especialidad.
                  </p>
                )}
              </div>

              <label>
                Especialidad principal
              </label>

              <select
                value={
                  form.primary_specialty_id
                }
                onChange={(event: any) =>
                  setForm((current) => ({
                    ...current,
                    primary_specialty_id:
                      event.target.value,
                  }))
                }
                disabled={
                  form.specialty_ids
                    .length === 0
                }
              >
                <option value="">
                  Sin especialidad principal
                </option>

                {specialties
                  .filter((specialty) =>
                    form.specialty_ids.includes(
                      idText(
                        specialty.id
                      )
                    )
                  )
                  .map((specialty) => (
                    <option
                      key={specialty.id}
                      value={idText(
                        specialty.id
                      )}
                    >
                      {specialty.name}
                    </option>
                  ))}
              </select>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <input
                  type="checkbox"
                      style={{
                        width: "auto",
                        flex: "0 0 auto",
                        margin: 0,
                      }}
                  checked={form.active}
                  onChange={(event: any) =>
                    setForm(
                      (current) => ({
                        ...current,
                        active:
                          event.target
                            .checked,
                      })
                    )
                  }
                />

                Profesional activo
              </label>

              <span />

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={closeForm}
                  disabled={submitting}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting
                    ? "Guardando..."
                    : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSpecialtyForm && (
        <div
          className="modal-backdrop"
          onClick={() => {
            if (!savingSpecialty) {
              setShowSpecialtyForm(
                false
              );
            }
          }}
        >
          <div
            className="modal"
            onClick={(
              event: any
            ) => event.stopPropagation()}
          >
            <h3>
              Nueva especialidad
            </h3>

            <p className="meta">
              La especialidad quedará disponible
              para todos los profesionales y para
              los filtros de Agenda.
            </p>

            <form
              className="form-grid"
              onSubmit={submitSpecialty}
            >
              <label>
                Nombre
              </label>

              <input
                value={specialtyName}
                onChange={(event: any) =>
                  setSpecialtyName(
                    event.target.value
                  )
                }
                placeholder="Ej: Cirugía plástica"
                required
              />

              <label>
                Categoría
              </label>

              <input
                value={
                  specialtyCategory
                }
                onChange={(event: any) =>
                  setSpecialtyCategory(
                    event.target.value
                  )
                }
                placeholder="Ej: Médica, estética, nutrición"
              />

              <label>
                Descripción
              </label>

              <textarea
                value={
                  specialtyDescription
                }
                onChange={(event: any) =>
                  setSpecialtyDescription(
                    event.target.value
                  )
                }
                placeholder="Descripción interna opcional"
                rows={3}
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={
                    savingSpecialty
                  }
                  onClick={() =>
                    setShowSpecialtyForm(
                      false
                    )
                  }
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    savingSpecialty
                  }
                >
                  {savingSpecialty
                    ? "Guardando..."
                    : "Crear especialidad"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
